import * as THREE from 'three';
import { placeBowOnString } from './bow.js';
import { DEVANAGARI, SWARA_NAMES } from './audio.js';

const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();

export function createPlayController({ camera, canvas, controls, sarangi, bow, audio, ui }) {
  let mode = 'look';
  let snapping = true;
  let selected = 0;
  let bowing = false;
  let lastPt = null;
  let energy = 0;
  let activeId = 'bow';
  const clock = new THREE.Clock();

  const hits = sarangi.userData.playStrings.map((s) => s.hit);

  function setMode(next) {
    mode = next;
    controls.enabled = next === 'look';
    canvas.style.cursor = next === 'play' ? 'crosshair' : 'grab';
    ui.setMode(next);
    if (next === 'look') {
      stopBow();
      restBow();
    }
  }

  function restBow() {
    bow.position.lerp(bow.userData.restPosition, 0.12);
    bow.rotation.x += (bow.userData.restRotation.x - bow.rotation.x) * 0.12;
    bow.rotation.y += (bow.userData.restRotation.y - bow.rotation.y) * 0.12;
    bow.rotation.z += (bow.userData.restRotation.z - bow.rotation.z) * 0.12;
  }

  function ndc(e) {
    const r = canvas.getBoundingClientRect();
    const x = ('touches' in e ? e.touches[0].clientX : e.clientX) - r.left;
    const y = ('touches' in e ? e.touches[0].clientY : e.clientY) - r.top;
    pointer.x = (x / r.width) * 2 - 1;
    pointer.y = -(y / r.height) * 2 + 1;
  }

  function pickString() {
    raycaster.setFromCamera(pointer, camera);
    const found = raycaster.intersectObjects(hits, false);
    if (found.length) {
      const idx = found[0].object.userData.stringIndex;
      selected = idx;
      ui.setString(idx);
      return { idx, point: found[0].point, uv: found[0].uv };
    }
    return null;
  }

  function pitchFromPoint(point, spec) {
    const { start, nut } = spec.points;
    const span = nut.clone().sub(start);
    const rel = point.clone().sub(start);
    const t = THREE.MathUtils.clamp(rel.dot(span) / span.lengthSq(), 0.02, 0.96);
    const open = audio.sa * spec.open;
    const max = open * 4;
    const stopped = open / Math.max(0.22, 1 - t * 0.78);
    let freq = THREE.MathUtils.clamp(stopped, open, max);
    if (snapping) freq = audio.snapFreq(freq);
    return freq;
  }

  function beginBow(e) {
    if (mode !== 'play') return;
    ndc(e);
    const hit = pickString();
    bowing = true;
    lastPt = hit?.point?.clone() || null;
    energy = 0.4;
    const spec = sarangi.userData.playStrings[selected];
    const freq = hit ? pitchFromPoint(hit.point, spec) : audio.freqFor(audio.raga.swaras[0]);
    audio.noteOn(activeId, freq, energy);
  }

  function moveBow(e) {
    if (mode !== 'play' || !bowing) return;
    ndc(e);
    const hit = pickString();
    const spec = sarangi.userData.playStrings[selected];
    const point = hit?.point || lastPt;
    if (!point) return;
    if (lastPt) {
      const d = point.distanceTo(lastPt);
      energy = THREE.MathUtils.clamp(energy * 0.85 + d * 18, 0.25, 1);
    }
    lastPt = point.clone();
    const freq = pitchFromPoint(point, spec);
    audio.noteOn(activeId, freq, energy);
    const tangent = spec.points.nut.clone().sub(spec.points.start).normalize();
    placeBowOnString(bow, point, tangent, energy);
    pulseString(spec, energy);
  }

  function stopBow() {
    if (!bowing) return;
    bowing = false;
    lastPt = null;
    energy = 0;
    audio.noteOff(activeId);
    sarangi.userData.playStrings.forEach((s) => {
      s.material.emissive = new THREE.Color(0x000000);
      s.material.emissiveIntensity = 0;
    });
  }

  function pulseString(spec, amp) {
    sarangi.userData.playStrings.forEach((s, i) => {
      const on = i === spec.index;
      s.material.emissive = new THREE.Color(on ? '#d26432' : '#000000');
      s.material.emissiveIntensity = on ? 0.18 + amp * 0.25 : 0;
    });
  }

  canvas.addEventListener('pointerdown', (e) => {
    audio.unlock();
    if (mode === 'play') {
      e.preventDefault();
      beginBow(e);
    }
  });
  canvas.addEventListener('pointermove', moveBow);
  window.addEventListener('pointerup', stopBow);
  canvas.addEventListener('pointerleave', stopBow);

  window.addEventListener('keydown', (e) => {
    if (e.repeat) return;
    if (e.code === 'Digit1') selectString(2);
    if (e.code === 'Digit2') selectString(1);
    if (e.code === 'Digit3') selectString(0);
    const map = audio.keyboardMap();
    const note = map[e.code];
    if (note) {
      audio.unlock();
      const freq = audio.freqFor(note.token);
      audio.noteOn(e.code, freq, 0.8);
      ui.pressSwara(note.token, true);
      const spec = sarangi.userData.playStrings[selected];
      pulseString(spec, 0.8);
      const info = { token: note.swara || note.token, name: SWARA_NAMES[note.swara || 'S'], deva: DEVANAGARI[note.swara || 'S'] };
      ui.showNote(info);
    }
  });

  window.addEventListener('keyup', (e) => {
    audio.noteOff(e.code);
    const map = audio.keyboardMap();
    const note = map[e.code];
    if (note) ui.pressSwara(note.token, false);
  });

  function selectString(i) {
    selected = i;
    ui.setString(i);
  }

  function triggerSwara(token) {
    audio.unlock();
    const freq = audio.freqFor(token);
    audio.noteOn('ui-' + token, freq, 0.82);
    const spec = sarangi.userData.playStrings[selected];
    pulseString(spec, 0.8);
    ui.showNote({
      token,
      name: SWARA_NAMES[token === 'S2' ? 'S' : token],
      deva: DEVANAGARI[token === 'S2' ? 'S' : token],
    });
  }

  function releaseSwara(token) {
    audio.noteOff('ui-' + token);
    if (!bowing) ui.showNote(null);
  }

  audio.onNote = (info, on) => {
    if (on && info) ui.showNote(info);
    else if (!bowing) ui.showNote(null);
  };

  function update() {
    const t = clock.getElapsedTime();
    if (mode === 'look' && !bowing) restBow();
    sarangi.userData.playStrings.forEach((s, i) => {
      if (s.material.emissiveIntensity > 0) {
        s.mesh.rotation.z = Math.sin(t * 48 + i) * 0.004 * s.material.emissiveIntensity;
      } else {
        s.mesh.rotation.z *= 0.8;
      }
    });
    sarangi.userData.sympathetics.forEach((m, i) => {
      const glow = audio.held?.size ? 0.15 : 0;
      void glow;
      m.position.x = Math.sin(t * 9 + i * 0.4) * 0.00015 * (bowing ? 4 : 1);
    });
  }

  return {
    setMode,
    setSnapping: (v) => {
      snapping = v;
    },
    selectString,
    triggerSwara,
    releaseSwara,
    update,
    get mode() {
      return mode;
    },
  };
}
